import React, { useState } from "react";
import { SENSAIPageContent, SENSAIPageContext } from "../types";

interface VerifyTabProps {
  content: SENSAIPageContent | null;
  context: SENSAIPageContext;
}

interface VerifyResult {
  checkId: string;
  status: "pass" | "fail" | "running";
  message: string;
  evidence?: string[];
  timestamp: string;
}

export const VerifyTab: React.FC<VerifyTabProps> = ({ content, context }) => {
  const [results, setResults] = useState<VerifyResult[]>([]);
  const [running, setRunning] = useState<string[]>([]);

  const runVerification = async (checkId: string) => {
    if (running.includes(checkId)) return;

    setRunning(prev => [...prev, checkId]);
    
    try {
      const response = await fetch('/api/sensai/verify', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pageId: context.pageId,
          checkId,
          context,
        }),
      });

      const result = await response.json();
      
      setResults(prev => {
        const filtered = prev.filter(r => r.checkId !== checkId);
        return [...filtered, {
          checkId,
          status: result.success ? "pass" : "fail",
          message: result.message,
          evidence: result.evidence,
          timestamp: new Date().toISOString(),
        }];
      });
    } catch (error) {
      setResults(prev => {
        const filtered = prev.filter(r => r.checkId !== checkId);
        return [...filtered, {
          checkId,
          status: "fail",
          message: "Verification failed to run",
          timestamp: new Date().toISOString(),
        }];
      });
    } finally {
      setRunning(prev => prev.filter(id => id !== checkId));
    }
  };

  const runAllVerifications = async () => {
    if (!content) return;
    
    for (const check of content.verify.checks) {
      await runVerification(check.id);
    }
  };

  if (!content || content.verify.checks.length === 0) {
    return (
      <div className="text-center text-slate-400">
        <div className="mb-4">🔍</div>
        <div className="text-sm">No verification checks available for this page.</div>
        {context.userRole === "admin" && (
          <button className="mt-3 px-3 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700">
            Add Checks
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-white text-sm">Data Verification</h3>
        <button
          onClick={runAllVerifications}
          disabled={running.length > 0}
          className="px-5 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all font-semibold disabled:opacity-50"
        >
          🔍 {running.length > 0 ? "Running..." : "Run All Checks"}
        </button>
      </div>

      <div className="space-y-4">
        {content.verify.checks.map((check) => {
          const result = results.find(r => r.checkId === check.id);
          const isRunning = running.includes(check.id);

          return (
            <div
              key={check.id}
              className="sensai-card p-4 rounded-xl"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="font-semibold text-white text-sm mb-2">{check.label}</h4>
                  <p className="text-slate-300 text-xs leading-relaxed">{check.description}</p>
                </div>
                <button
                  onClick={() => runVerification(check.id)}
                  disabled={isRunning}
                  className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white text-xs rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all font-medium disabled:opacity-50"
                >
                  {isRunning ? "..." : "Run"}
                </button>
              </div>

              {result && (
                <div className={`mt-3 p-2 rounded text-xs ${
                  result.status === "pass" 
                    ? "bg-green-900/50 border border-green-700 text-green-300"
                    : "bg-red-900/50 border border-red-700 text-red-300"
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span>{result.status === "pass" ? "✅" : "❌"}</span>
                    <span className="font-medium">
                      {result.status === "pass" ? "PASS" : "FAIL"}
                    </span>
                    <span className="text-slate-400">
                      {new Date(result.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <div>{result.message}</div>
                  {result.evidence && result.evidence.length > 0 && (
                    <div className="mt-2">
                      <div className="font-medium mb-1">Evidence:</div>
                      <ul className="list-disc list-inside space-y-1">
                        {result.evidence.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {content.actions.length > 0 && (
        <div className="border-t border-slate-700 pt-4">
          <h3 className="font-semibold text-white text-sm mb-3">Quick Actions</h3>
          <div className="space-y-2">
            {content.actions.map((action) => (
              <button
                key={action.id}
                className="w-full px-3 py-2 bg-slate-700 text-white text-sm rounded hover:bg-slate-600 transition-colors text-left"
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
